import PropTypes from 'prop-types'
import React, { Component } from 'react'
import css from './Popover.module.css'
import { Link } from 'react-router'
import TransitionGroup from 'react-transition-group/TransitionGroup'
const ANIMATION_DURATION = 220

/* ===== PopoverBox ===== */

class PopoverBox extends Component {
  render () {
    const { children } = this.props

    return (
      <div
        className={css.position}
        ref={(node) => { this.position = node }}
        data-position='bottom'
        data-alignment='center'>

        <div className={css.animation} ref={(node) => { this.animation = node }}>
          <div className={css.content}>
            {children}
          </div>
        </div>
      </div>
    )
  }

  componentWillEnter (callback) {
    // Smartly position the popover when it overflows the body
    const popoverRect = this.position.getBoundingClientRect()
    const wrapperRect = this.position.parentNode.parentNode.getBoundingClientRect()
    const bodyWidth = document.body.clientWidth
    const bodyHeight = document.body.clientHeight
    let position = 'bottom'
    let alignment = 'center'
    let transformOriginX = '0'
    let transformOriginY = '0'

    if ((popoverRect.top + popoverRect.height + window.scrollY) > bodyHeight) {
      position = 'top'
    }

    if (popoverRect.left < 0) {
      alignment = 'left'
    } else if ((popoverRect.left + popoverRect.width + window.scrollX) > bodyWidth) {
      alignment = 'right'
    }

    if (alignment === 'left') {
      transformOriginX = `${wrapperRect.width / 2}px`
    } else if (alignment === 'right') {
      transformOriginX = `${popoverRect.width - (wrapperRect.width / 2)}px`
    } else if (alignment === 'center') {
      transformOriginX = '50%'
    }

    if (position === 'top') {
      transformOriginY = `100%`
    } else if (position === 'bottom') {
      transformOriginY = `0`
    }

    this.position.setAttribute('data-position', position)
    this.position.setAttribute('data-alignment', alignment)
    this.animation.style.transformOrigin = `${transformOriginX} ${transformOriginY}`

    // Allow style recalculation before triggering the animation
    setTimeout(callback, 17)
  }

  componentDidEnter () {
    this.position.classList.add(css.show)
  }

  componentWillLeave (callback) {
    this.position.classList.remove(css.show)
    setTimeout(callback, ANIMATION_DURATION)
  }
}

PopoverBox.propTypes = {
  children: PropTypes.node.isRequired
}

PopoverBox.displayName = 'PopoverBox'

/* ===== Popover ===== */

class Popover extends Component {
  constructor () {
    super()

    this.state = {
      isOpen: false
    }

    this.onButtonClick = this.onButtonClick.bind(this)
    this.onBodyClick = this.onBodyClick.bind(this)
    this.onEsc = this.onEsc.bind(this)
  }

  render () {
    const { children, content, openClass } = this.props
    const { isOpen } = this.state
    let button = children

    if (isOpen && openClass) {
      button = React.cloneElement(button, {
        className: `${button.props.className} ${openClass}`
      })
    }

    return (
      <div className={css.wrapper} ref={(node) => { this.wrapper = node }}>
        <div onClick={this.onButtonClick}>
          {button}
        </div>

        <TransitionGroup>
          {isOpen && <PopoverBox>{content}</PopoverBox>}
        </TransitionGroup>
      </div>
    )
  }

  componentWillUnmount () {
    document.body.removeEventListener('click', this.onBodyClick, false)
    document.body.removeEventListener('keydown', this.onEsc, false)
  }

  onButtonClick (e) {
    const isOpen = !this.state.isOpen
    if (isOpen) {
      this.open()
    } else {
      this.close()
    }

    this.setState({ isOpen })
  }

  onBodyClick (e) {
    // Ignore clicks on the popover or button
    if (this.wrapper === e.target || this.wrapper.contains(e.target)) {
      return
    }

    this.close()
  }

  onEsc (e) {
    // esc key
    if (e.keyCode === 27) {
      this.close()
    }
  }

  open () {
    if (this.state.isOpen) {
      return
    }

    this.setState({ isOpen: true })
    document.body.addEventListener('click', this.onBodyClick, false)
    document.body.addEventListener('keydown', this.onEsc, false)

    if (this.props.onOpen) {
      this.props.onOpen()
    }
  }

  close () {
    if (!this.state.isOpen) {
      return
    }

    this.setState({ isOpen: false })
    document.body.removeEventListener('click', this.onBodyClick, false)
    document.body.removeEventListener('keydown', this.onEsc, false)

    if (this.props.onClose) {
      this.props.onClose()
    }
  }
}

Popover.propTypes = {
  children: PropTypes.element.isRequired,
  content: PropTypes.node.isRequired,
  openClass: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func
}

Popover.displayName = 'Popover'

export default Popover

/* ===== PopoverGroup ===== */

export function PopoverGroup ({ children }) {
  return (
    <div className={css.group}>
      {children}
    </div>
  )
}

PopoverGroup.propTypes = {
  children: PropTypes.node.isRequired
}

PopoverGroup.displayName = 'PopoverGroup'

/* ===== PopoverLink ===== */

function PopoverLink ({ children, href, onClick, rel, className }) {
  const attrs = {
    className: `${css.link} ${className}`,
    onClick
  }

  if (href) {
    // Don't pass hash links to react-router because it navigates to the root (/)
    if (rel === 'external' || href.startsWith('#')) {
      attrs.href = href
    } else {
      attrs.to = href
    }

    return <Link {...attrs}>{children}</Link>
  }

  return (
    <button {...attrs}>{children}</button>
  )
}

PopoverLink.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
  rel: PropTypes.string,
  className: PropTypes.string.isRequired
}

PopoverLink.displayName = 'PopoverLink'

/* ===== PopoverListLink ===== */

export function PopoverListLink (props) {
  return (
    <PopoverLink {...props} className={css.listLink} />
  )
}

PopoverListLink.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
  rel: PropTypes.string
}

PopoverListLink.displayName = 'PopoverListLink'

/* ===== PopoverCtaLink ===== */

export function PopoverCtaLink (props) {
  return (
    <PopoverLink {...props} className={css.ctaLink} />
  )
}

PopoverCtaLink.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
  rel: PropTypes.string
}

PopoverCtaLink.displayName = 'PopoverCtaLink'



// WEBPACK FOOTER //
// ./ui/Popover.js